import { createContext, useContext } from 'react';
import type { Slot } from '../PaymentStructureGrid/types';

export interface GridMetaValue {
    isLoading: boolean;
    isComplete: boolean;
    hasErrors: boolean;
    allFailed: boolean;
    errorCount: number;
}

export const MetaContext = createContext<GridMetaValue | undefined>(undefined);

export const useGridMeta = (): GridMetaValue => {
    const value = useContext(MetaContext);
    if (!value) {
        throw new Error('useGridMeta must be used within an AltDealStructureDataProvider');
    }
    return value;
};

const termColumnKey = (slot: Slot): string => `${slot.dealType}-${slot.term}`;

/**
 * Group slots by their grid (`dealType`). The unavailable-column / total-outage detection must run
 * per grid: the finance and lease grids fail independently, so a healthy lease grid must not mask a
 * fully-failed finance grid (and vice-versa). Column/row keys are already `dealType`-namespaced, so
 * the only thing that has to be scoped is the "at least one cell is ready" total-outage guard.
 */
const groupSlotsByDealType = (slots: Slot[]): Slot[][] => {
    const groups = new Map<Slot['dealType'], Slot[]>();
    for (const slot of slots) {
        const group = groups.get(slot.dealType);
        if (group) group.push(slot);
        else groups.set(slot.dealType, [slot]);
    }
    return [...groups.values()];
};

/**
 * Term columns that came back fully unavailable — no `ready` cell and nothing still `pending` — e.g.
 * an invalid term the user typed, or one the dealer doesn't offer. Their failed cells are NOT
 * retryable (retrying re-requests the same unavailable term), so they're excluded from the
 * error/retry banner. Detection runs per grid (`dealType`): a column is only treated as unavailable
 * when its own grid has at least one `ready` cell, so a grid-level total outage (nothing ready in
 * that grid) still surfaces the retry banner even when the other grid is healthy.
 */
export const getUnavailableTermColumns = (slots: Slot[]): Set<string> => {
    const unavailableColumns = new Set<string>();
    for (const gridSlots of groupSlotsByDealType(slots)) {
        if (!gridSlots.some((slot) => slot.status === 'ready')) continue;
        const readyColumns = new Set<string>();
        const pendingColumns = new Set<string>();
        const allColumns = new Set<string>();
        for (const slot of gridSlots) {
            const columnKey = termColumnKey(slot);
            allColumns.add(columnKey);
            if (slot.status === 'ready') readyColumns.add(columnKey);
            if (slot.status === 'pending') pendingColumns.add(columnKey);
        }
        allColumns.forEach((columnKey) => {
            if (!readyColumns.has(columnKey) && !pendingColumns.has(columnKey)) unavailableColumns.add(columnKey);
        });
    }
    return unavailableColumns;
};

const downPaymentRowKey = (slot: Slot): string => `${slot.dealType}-${slot.downPayment}`;

/**
 * Down-payment rows the user explicitly entered (`editedDownPaymentKeys`) that came back fully
 * unavailable — an invalid down payment. Retrying re-requests the same invalid value, so they're
 * excluded from the retry banner. We require the user-typed signal because a fully-empty row also
 * describes a transient whole-row failure, which IS retryable (the retry unit is the down-payment
 * row). Detection runs per grid (`dealType`): a row is only treated as unavailable when its own grid
 * has at least one `ready` cell, so a grid-level total outage still surfaces retry.
 */
export const getUnavailableEditedDownPaymentRows = (slots: Slot[], editedDownPaymentKeys: Set<string>): Set<string> => {
    if (editedDownPaymentKeys.size === 0) return new Set();
    const unavailableRows = new Set<string>();
    for (const gridSlots of groupSlotsByDealType(slots)) {
        if (!gridSlots.some((slot) => slot.status === 'ready')) continue;
        const readyRows = new Set<string>();
        const pendingRows = new Set<string>();
        const allRows = new Set<string>();
        for (const slot of gridSlots) {
            const rowKey = downPaymentRowKey(slot);
            allRows.add(rowKey);
            if (slot.status === 'ready') readyRows.add(rowKey);
            if (slot.status === 'pending') pendingRows.add(rowKey);
        }
        allRows.forEach((rowKey) => {
            if (editedDownPaymentKeys.has(rowKey) && !readyRows.has(rowKey) && !pendingRows.has(rowKey))
                unavailableRows.add(rowKey);
        });
    }
    return unavailableRows;
};

/**
 * A failed/timed-out cell worth retrying — not part of an unavailable (invalid) term column or an
 * unavailable (user-entered invalid) down-payment row.
 */
export const isRetryableFailedSlot = (
    slot: Slot,
    unavailableColumns: Set<string>,
    unavailableRows: Set<string> = new Set()
): boolean =>
    (slot.status === 'failed' || slot.status === 'timed-out') &&
    !unavailableColumns.has(termColumnKey(slot)) &&
    !unavailableRows.has(downPaymentRowKey(slot));

export const computeGridMeta = (slots: Slot[]): GridMetaValue => {
    const unavailableColumns = getUnavailableTermColumns(slots);
    let isLoading = false;
    let errorCount = 0;
    for (const slot of slots) {
        if (slot.status === 'pending') isLoading = true;
        if (isRetryableFailedSlot(slot, unavailableColumns)) errorCount++;
    }
    const isComplete = slots.length > 0 && !isLoading;
    const hasErrors = errorCount > 0;
    const allFailed = slots.length > 0 && errorCount === slots.length;
    return { isLoading, isComplete, hasErrors, allFailed, errorCount };
};
